import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCategoryQuery, useCreateCategoryMutation, useUpdateCategoryMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';
import { getImageUrl } from '../../../utils/imageHelpers';

const AdminCategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetCategoryQuery(id!, { skip: !isEdit });
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    image: null as File | null,
    banner: null as File | null,
  });

  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);
  const [existingBanner, setExistingBanner] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const category = data.data;
      setFormData({
        title: category.title || category.name || '',
        title_ar: category.title_ar || category.name_ar || '',
        image: null,
        banner: null,
      });
      if (category.image) {
        // Extract filename from full URL if needed
        const imageFilename = category.image.includes('/uploads/') 
          ? category.image.split('/uploads/')[1] 
          : category.image;
        setExistingImage(imageFilename);
      } else {
        setExistingImage(undefined);
      }
      if (category.banner) {
        // Extract filename from full URL if needed
        const bannerFilename = category.banner.includes('/uploads/') 
          ? category.banner.split('/uploads/')[1] 
          : category.banner;
        setExistingBanner(bannerFilename);
      } else {
        setExistingBanner(undefined);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleBannerChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, banner: file }));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
  };

  const handleRemoveExistingBanner = () => {
    setExistingBanner(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('title_ar', formData.title_ar);

      if (formData.image) {
        submitData.append('image', formData.image);
      }

      if (formData.banner) {
        submitData.append('banner', formData.banner);
      }

      if (isEdit) {
        const result = await updateCategory({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Category updated successfully!'));
      } else {
        const result = await createCategory(submitData).unwrap();
        showSuccess(result.message || t('Category created successfully!'));
      }
      
      navigate('/admin/categories');
    } catch (err: any) {
      showError(err?.data?.message || t(isEdit ? 'Failed to update category. Please try again.' : 'Failed to create category. Please try again.'));
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Category') : t('Add New Category')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/categories'); }} className="text-muted text-hover-primary">{t('Categories')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Category Title')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Category Title in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <ImageUpload
                        existingImage={existingImage}
                        onImageChange={handleImageChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        label={t('Upload Picture')}
                        name="image"
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <ImageUpload
                        existingImage={existingBanner}
                        onImageChange={handleBannerChange}
                        onRemoveExisting={handleRemoveExistingBanner}
                        label={t('Upload Banner')}
                        name="banner"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/categories')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCategoryForm;




